import { HOPE_PAYMENT_MAX_AGE_MS, HopePaymentBasis, HopePaymentRecord, hopePaymentPricing, readHopePaymentRecord } from '../../../../services/payment-calc';
import { CalcPaymentsAnswers, calcPaymentsBucket, calcPaymentsLoan, calcPaymentsPurpose } from './calc-payments';

// FRU-23777: since the mortgage calculator showed each visitor's own payment (10/2), calculator ad visitors submit far less on
// Hope than on the original form, and most leave a step or two after seeing it; before 10/2 Hope's calculator skin showed no
// payment and converted better than the original. This test keeps the calculator for everyone and splits it 50/50 by visitor:
// "live" shows the payment as today; "held" works it out the same way but shows "Calculated" until the Match page, where the
// visitor's lender matches reveal it (Ed's 9/29 direction for the calculator payments test).
//
// Both versions are identical until the visitor's own payment would first appear: the same keys, amounts and rates, and the
// same worked example on the first screen (an example, labelled as one, not the visitor's payment).

/** On (FRU-23777). The held snapshot survives a reload since www-ng #1593. mc_payment=held|live forces a version for QA
 *  either way. Matomo experiment FRU-23777-HopeMortgageCalcPayment is id 503. */
export const HOPE_MC_PAYMENT_TEST_ENABLED = true;
export const MC_PAYMENT_SALT = 'mc-payment-held-v1';
export const MC_PAYMENT_SEEN_STORAGE_KEY = 'hope_mc_payment_seen_v1';
export const MC_PAYMENT_EXPERIMENT = 'FRU-23777-HopeMortgageCalcPayment';
export type McPaymentArm = 'live' | 'held';

/**
 * Which version this chat gets, decided once when it starts, or null when it isn't in the test: no mortgage calculator, no
 * visitor id (tracking opted out), or the test is off with no override.
 */
export function mcPaymentArmFor(opts: { mortgageCalc: boolean; search: string; visitorId: string; enabled?: boolean }): McPaymentArm | null {
  if (!opts.mortgageCalc) { return null; }
  let override: string | null = null;
  try { override = new URLSearchParams(opts.search || '').get('mc_payment'); } catch (e) { return null; }
  if (override === 'held' || override === 'live') { return override; }
  const enabled = opts.enabled === undefined ? HOPE_MC_PAYMENT_TEST_ENABLED : opts.enabled;
  if (!enabled || !opts.visitorId) { return null; }
  return calcPaymentsBucket(opts.visitorId, MC_PAYMENT_SALT) < 50 ? 'held' : 'live';
}

/** A forced version is QA: it stays out of Matomo and the lead's experiment row. */
export function mcPaymentForced(search: string): boolean {
  try { return new URLSearchParams(search || '').has('mc_payment'); } catch (e) { return true; }
}

export function mcPaymentVariationName(arm: McPaymentArm): string {
  return arm === 'held' ? 'HeldUntilMatches' : 'original';
}

/** Reload-safe proof that this exact visitor saw this exact version in this tab. */
export function mcPaymentEnrollmentMarker(visitorId: string, arm: McPaymentArm | null): string {
  if (!visitorId || !arm) { return ''; }
  return [MC_PAYMENT_SALT, visitorId, arm].join('|');
}

/**
 * A reload keeps the held version's priced snapshot (no contact detail, no lead id) in the tab, tied to the enrollment that made it,
 * so the payment the visitor was told is calculated still unlocks on the Match page after a refresh. The binding is the
 * enrollment marker, or for a forced QA version the version alone. Read back only for the same binding, inside the Match page's
 * own 2-hour window, and only when the Match page's reader accepts it with its price; anything else is dropped.
 */
export const MC_PAYMENT_PENDING_STORAGE_KEY = 'hope_mc_payment_pending_v1';
export function mcPaymentPendingBinding(arm: McPaymentArm | null, marker: string): string {
  if (arm !== 'held') { return ''; }
  return marker || 'qa|' + arm;
}
export function mcPaymentPendingSaved(rec: HopePaymentRecord | null, binding: string, now: number): string | null {
  if (!rec || !binding || !rec.priced || (rec as any).lead !== undefined) { return null; }
  return JSON.stringify({ v: 1, binding, savedAt: now, rec });
}
export function mcPaymentPendingRestored(raw: string | null, binding: string, now: number): HopePaymentRecord | null {
  if (!raw || !binding) { return null; }
  let saved: any = null;
  try { saved = JSON.parse(raw); } catch (e) { return null; }
  if (!saved || typeof saved !== 'object' || saved.v !== 1 || saved.binding !== binding) { return null; }
  const at = Number(saved.savedAt);
  if (!Number.isFinite(at) || at > now + 60000 || now - at > HOPE_PAYMENT_MAX_AGE_MS) { return null; }
  const rec = saved.rec;
  if (!rec || typeof rec !== 'object' || rec.lead !== undefined) { return null; }
  return mcPaymentReadable(rec, now);
}

/** The lead's experiment row, for a visitor whose first calculator screen was shown in either version. */
export function mcPaymentLeadExperiment(arm: McPaymentArm | null, screenLogged: boolean, eligible: boolean):
  { experiment: string; variation: string; hit_segment: boolean } | null {
  if (!arm || !screenLogged || !eligible) { return null; }
  return { experiment: MC_PAYMENT_EXPERIMENT, variation: mcPaymentVariationName(arm), hit_segment: true };
}

/**
 * The record the Match page reveals in the held version, priced exactly as the calculator would have shown it: the loan the
 * keys made and the pair on the display (rate and APR as displayed, with the reviewed revision, offer and disclosure they came
 * from). Null when anything is missing, so nothing is promised that the Match page can't show.
 */
export function mcPaymentSnapshot(
  mode: 'buy' | 'refi' | null,
  loan: number,
  pair: { rate: string; apr: string; revision_id?: any; offer_sha256?: any; disclosure?: any } | null,
  now: number,
): HopePaymentRecord | null {
  if ((mode !== 'buy' && mode !== 'refi') || !(loan > 0) || !pair) { return null; }
  const priced = hopePaymentPricing(loan, pair);
  if (!priced || !priced.revisionId || !priced.offerSha256) { return null; }
  const basis: HopePaymentBasis = mode === 'buy' ? 'price_minus_down' : 'balance';
  return mcPaymentReadable({ v: 1, arm: 'payments', purpose: mode === 'buy' ? 'purchase' : 'refinance', product: '30yr_fixed',
    loanAmount: Math.round(loan), basis, at: now, priced }, now);
}

/** Only a record the Match page's own reader accepts with its price (review: a typed $15M price made a loan the reader
 *  rejects, so "Calculated" promised a reveal that could never show). Anything else is null and the amount shows as today. */
function mcPaymentReadable(rec: HopePaymentRecord, now: number): HopePaymentRecord | null {
  const read = readHopePaymentRecord(JSON.stringify({ ...rec, lead: '1' }), now);
  return read && read.priced && read.priced.payment === (rec.priced && rec.priced.payment) ? rec : null;
}

/**
 * The held snapshot as it stands for the answers the lead will carry, at the same pair (rate, APR, revision, offer and
 * disclosure the display used). Review: an amount corrected after the calculator (Hope setting a new home price or balance)
 * left the snapshot on the old loan, so the Match page would show the old payment. Repriced from the current answers by the
 * same rule the submitted lead uses; null when the loan type changed or the loan can't be priced and shown.
 */
export function mcPaymentCurrent(pending: HopePaymentRecord | null, lpButton: any, answers: CalcPaymentsAnswers, now: number): HopePaymentRecord | null {
  if (!pending || !pending.priced) { return null; }
  // The snapshot's own age first (review): repricing stamps it with now, so a snapshot past the Match page's window would
  // otherwise come back as a fresh record with the old rate pair.
  if (!mcPaymentReadable(pending, now)) { return null; }
  const purpose = calcPaymentsPurpose(lpButton);
  if (!purpose || purpose !== pending.purpose) { return null; }
  const loan = calcPaymentsLoan(purpose, answers);
  if (!loan) { return null; }
  const p = pending.priced;
  const priced = hopePaymentPricing(loan.loanAmount, { rate: p.rate, apr: p.apr, revision_id: p.revisionId, offer_sha256: p.offerSha256,
    disclosureUrl: p.disclosureUrl });
  if (!priced || !priced.revisionId || !priced.offerSha256) { return null; }
  return mcPaymentReadable({ ...pending, loanAmount: Math.round(loan.loanAmount), basis: loan.basis, at: now, priced }, now);
}

/** Enters the visitor into the Matomo experiment with the version this code assigned. Forced versions stay out. Never throws. */
export function reportMcPaymentVariation(arm: McPaymentArm, search: string): void {
  const w: any = typeof window !== 'undefined' ? window : null;
  if (!w || mcPaymentForced(search)) { return; }
  const variation = mcPaymentVariationName(arm);
  const enter = () => {
    try {
      const exp = new w.Matomo.AbTesting.Experiment({
        name: MC_PAYMENT_EXPERIMENT,
        variations: ['original', 'HeldUntilMatches'].map(name => ({ name, activate: () => { /* Hope renders it itself */ } })),
        trigger: () => false,
      });
      exp.forceVariation(variation);
    } catch (e) { /* non-fatal */ }
  };
  try {
    if (typeof w.Piwik === 'object' && w.Matomo && typeof w.Matomo.AbTesting === 'object') { enter(); return; }
    const prev = w.matomoAbTestingAsyncInit;
    w.matomoAbTestingAsyncInit = function () { if (typeof prev === 'function') { try { prev(); } catch (e) { /* keep going */ } } enter(); };
  } catch (e) { /* non-fatal */ }
}
